import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import { useTranslation } from "react-i18next"
import type { GroupStyleI, GroupStyleT } from "tweenly"
import ColorField from "../../../common/PropertyTools/ColorField"
import CompactSelect from "../../../common/PropertyTools/CompactSelect"
import ImageInput from "../../../common/PropertyTools/ImageInput"
import PrefixedInput from "../../../common/PropertyTools/PrefixedInput"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"

interface Props {
    fill: GroupStyleI
    properties: GroupStyleT[]
    handleOnChange: (field: "property" | "value", value: any) => void
    handleDelete: () => void
    handleSetStyleValue: (property: any, value: any) => void
    handleSetAttributeValue: (property: any, value: any) => void
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

/**
 * One fill, Figma style: its type with the remove button, then its value — a color as one
 * compact row (swatch, hex, opacity), a gradient as its CSS with a preview swatch.
 */
const FillRow = ({
    fill,
    properties,
    handleOnChange,
    handleDelete,
    handleSetStyleValue,
    handleSetAttributeValue,
}: Props) => {
    const { t } = useTranslation()

    // an image fill is only offered to old graphics that already have one
    const options = properties
        .filter((property) =>
            fill.property === "backgroundImage" ? true : property.input.label !== "image"
        )
        .map((property) => ({
            value: property.property,
            label: capitalize(property.input.label ?? property.property),
        }))

    const handleValueChange = (e: any) => handleOnChange("value", e.target.value)

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            {/* the remove button sits in the narrow column at the end, as in Figma */}
            <PropertyRow
                gutter={
                    <Tooltip title={`${t("editor:remove")} ${fill.property}`}>
                        <IconButton size="small" onClick={handleDelete}>
                            <RemoveRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                }
            >
                <Box display="flex" gap={1}>
                    <Box flex={1} minWidth={0}>
                        <CompactSelect
                            name="type"
                            value={fill.property}
                            options={options}
                            label="type"
                            tooltip="Fill type"
                            handleOnChange={(e) => handleOnChange("property", e.target.value)}
                        />
                    </Box>
                    <Box flex={1} />
                </Box>
            </PropertyRow>

            <PropertyRow>
                {fill.property === "backgroundColor" && (
                    <ColorField styleProperty={fill} handleOnChange={handleValueChange} />
                )}
                {fill.property === "linearGradient" && (
                    <PrefixedInput
                        name="linearGradient"
                        value={fill.value}
                        type="text"
                        label="linear gradient"
                        prefix={
                            <Box
                                sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "2px",
                                    border: 1,
                                    borderColor: "text.secondary",
                                    background: `linear-gradient(${fill.value})`,
                                }}
                            />
                        }
                        tooltip="Linear gradient (CSS: angle, color stops)"
                        handleOnChange={handleValueChange}
                    />
                )}
                {fill.property === "backgroundImage" && (
                    <ImageInput
                        styleProperty={fill}
                        handleOnChange={handleValueChange}
                        handleSetStyleValue={handleSetStyleValue}
                        handleSetAttributeValue={handleSetAttributeValue}
                    />
                )}
            </PropertyRow>
        </Box>
    )
}

export default FillRow
